import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import {
  TrendingUp, Landmark, TrendingDown, Activity, ArrowUpRight, ArrowDownRight, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);

const ASSET_TYPE_LABELS = { bank_account: 'Cash & Savings', cash: 'Cash & Savings', vehicle: 'Vehicles', metal: 'Precious Metals', stock: 'Investments', crypto: 'Crypto', real_estate: 'Real Estate', valuable: 'Other Assets', other: 'Other Assets' };
const PIE_COLORS = ['#3B82F6', '#F59E0B', '#8B5CF6', '#EC4899', '#10B981', '#6366F1', '#EF4444'];

export default function PersonalHomePage() {
  const { currentCompany } = useCompany();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [liabilitySummary, setLiabilitySummary] = useState(null);
  const [txnSummary, setTxnSummary] = useState(null);
  const [recentTxns, setRecentTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!currentCompany) return;
    setLoading(true);
    try {
      const [assetRes, liabRes, txnRes, recentRes] = await Promise.all([
        axios.get(`${API}/api/assets/summary`, { params: { company_id: currentCompany.id }, withCredentials: true }),
        axios.get(`${API}/api/liabilities/summary`, { params: { company_id: currentCompany.id }, withCredentials: true }),
        axios.get(`${API}/api/transactions/summary`, { params: { company_id: currentCompany.id }, withCredentials: true }),
        axios.get(`${API}/api/transactions`, { params: { company_id: currentCompany.id }, withCredentials: true }),
      ]);
      setSummary(assetRes.data);
      setLiabilitySummary(liabRes.data);
      setTxnSummary(txnRes.data);
      setRecentTxns(recentRes.data.slice(0, 5));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [currentCompany]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!currentCompany) return null;
  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;

  const netWorth = summary?.total_net_worth || 0;
  const totalAssets = summary?.total_assets || 0;
  const totalLiabilities = liabilitySummary?.total_liabilities || 0;
  const debtRatio = totalAssets > 0 ? Math.round((totalLiabilities / totalAssets) * 100) : 0;

  // Build asset breakdown for pie chart
  const assetBreakdown = {};
  (summary?.by_type || []).forEach(t => {
    const label = ASSET_TYPE_LABELS[t.type] || 'Other Assets';
    assetBreakdown[label] = (assetBreakdown[label] || 0) + t.value;
  });
  const pieData = Object.entries(assetBreakdown).map(([name, value]) => ({ name, value })).filter(d => d.value > 0).sort((a, b) => b.value - a.value);

  // Liability pie data
  const liabPieData = (liabilitySummary?.by_type || []).map(t => ({
    name: t.type === 'credit_card' ? 'Credit Cards' : t.type === 'loan' ? 'Loans' : t.type === 'mortgage' ? 'Mortgages' : 'Other',
    value: t.value
  })).filter(d => d.value > 0);

  return (
    <div className="space-y-6" data-testid="personal-home">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={TrendingUp} iconBg="bg-green-50" iconColor="text-green-600" label="Net Worth" value={fmtCurrency(netWorth)} />
        <StatCard icon={Landmark} iconBg="bg-blue-50" iconColor="text-blue-600" label="Total Assets" value={fmtCurrency(totalAssets)} />
        <StatCard icon={TrendingDown} iconBg="bg-red-50" iconColor="text-red-600" label="Total Liabilities" value={fmtCurrency(totalLiabilities)} />
        <StatCard icon={Activity} iconBg="bg-purple-50" iconColor="text-purple-600" label="Debt Ratio" value={`${debtRatio}%`} badge={debtRatio < 30 ? 'Healthy' : debtRatio < 60 ? 'Moderate' : 'High'} badgeColor={debtRatio < 30 ? 'text-green-600' : debtRatio < 60 ? 'text-yellow-600' : 'text-red-600'} />
      </div>

      {/* Row 2: Net Worth Timeline + Liabilities Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Net Worth Timeline */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900">Net Worth Timeline</h3>
          <p className="text-sm text-gray-500 mb-4">Your financial growth over 6 months</p>
          {summary?.history?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={summary.history}>
                <defs>
                  <linearGradient id="nwFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="date" tick={{ fill: '#6B7280', fontSize: 11 }} tickFormatter={v => { const d = new Date(v); return d.toLocaleString('default', { month: 'short' }); }} />
                <YAxis tick={{ fill: '#6B7280', fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={v => fmtCurrency(v)} labelFormatter={v => v} contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }} />
                <Area type="monotone" dataKey="net_worth" name="Net Worth" stroke="#10B981" fill="url(#nwFill)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">Timeline will build as you add data</div>
          )}
        </div>

        {/* Liabilities Overview */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900">Liabilities Overview</h3>
          <p className="text-sm text-gray-500 mb-4">Breakdown of your debts and liabilities</p>
          {liabPieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={liabPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                  {liabPieData.map((_, i) => <Cell key={i} fill={['#EF4444', '#F59E0B', '#6366F1', '#8B5CF6'][i % 4]} />)}
                </Pie>
                <Tooltip formatter={v => fmtCurrency(v)} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">No liabilities recorded</div>
          )}
        </div>
      </div>

      {/* Row 3: Monthly Cash Flow + Spending by Category */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Cash Flow */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900">Monthly Cash Flow</h3>
          <p className="text-sm text-gray-500 mb-4">Income vs expenses over selected period</p>
          {txnSummary?.trends?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={txnSummary.trends.filter(t => t.income > 0 || t.expenses > 0)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="month" tick={{ fill: '#6B7280', fontSize: 11 }} tickFormatter={v => { const parts = v.split('-'); return new Date(parts[0], parts[1]-1).toLocaleString('default', { month: 'short' }); }} />
                <YAxis tick={{ fill: '#6B7280', fontSize: 11 }} tickFormatter={v => `$${v}`} />
                <Tooltip formatter={v => fmtCurrency(v)} contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }} />
                <Legend />
                <Bar dataKey="income" name="Income" fill="#10B981" radius={[4,4,0,0]} />
                <Bar dataKey="expenses" name="Expenses" fill="#EF4444" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">Add transactions to see cash flow</div>
          )}
        </div>

        {/* Spending by Category */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900">Spending by Category</h3>
          <p className="text-sm text-gray-500 mb-4">Top expense categories for selected period</p>
          {txnSummary?.by_category?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={txnSummary.by_category.slice(0, 5)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis type="number" tick={{ fill: '#6B7280', fontSize: 11 }} tickFormatter={v => `$${v}`} />
                <YAxis type="category" dataKey="category" tick={{ fill: '#6B7280', fontSize: 11 }} width={80} />
                <Tooltip formatter={v => fmtCurrency(v)} contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }} />
                <Bar dataKey="spent" name="Spent" fill="#EF4444" radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">No spending data yet</div>
          )}
        </div>
      </div>

      {/* Row 4: Total Asset Breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900">Total Asset Breakdown</h3>
        <p className="text-sm text-gray-500 mb-4">Distribution of your {fmtCurrency(totalAssets)} in total assets</p>
        <div className="flex flex-col lg:flex-row items-center gap-8">
          {pieData.length > 0 ? (
            <>
              <div className="w-64 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`} labelLine={true}>
                      {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={v => fmtCurrency(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-3 w-full">
                {pieData.map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-sm font-medium text-gray-700">{item.name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-900">{fmtCurrency(item.value)}</p>
                      <p className="text-xs text-gray-500">{totalAssets > 0 ? ((item.value / totalAssets) * 100).toFixed(1) : 0}%</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="w-full text-center py-8 text-gray-400">Add assets to see breakdown</div>
          )}
        </div>
      </div>

      {/* Row 5: Quick Access + Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Access */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900">Quick Access</h3>
          <p className="text-sm text-gray-500 mb-4">Your pinned accounts</p>
          <div className="space-y-3">
            <button onClick={() => navigate('/personal/assets/financial')} className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-testid="quick-financial">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><Landmark className="w-5 h-5 text-blue-600" /></div>
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">Financial Accounts</p>
                  <p className="text-xs text-gray-500">{summary?.asset_count || 0} accounts</p>
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-gray-400" />
            </button>
            <button onClick={() => navigate('/personal/assets/vehicles')} className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-testid="quick-vehicles">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center"><Activity className="w-5 h-5 text-amber-600" /></div>
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">Vehicles</p>
                  <p className="text-xs text-gray-500">Manage vehicles</p>
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-gray-400" />
            </button>
          </div>
          <button onClick={() => navigate('/personal/assets')} className="w-full mt-4 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2">
            Manage Accounts <ExternalLink className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
          <p className="text-sm text-gray-500 mb-4">Latest transactions</p>
          {recentTxns.length > 0 ? (
            <div className="space-y-0">
              {recentTxns.map((txn, i) => (
                <div key={txn.id || i} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{txn.description}</p>
                    <p className="text-xs text-gray-500">{txn.date}</p>
                  </div>
                  <span className={`text-sm font-semibold ${txn.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                    {txn.type === 'income' ? '+' : ''}{fmtCurrency(txn.amount)}
                  </span>
                </div>
              ))}
              <button onClick={() => navigate('/personal/spending')} className="w-full mt-3 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2">
                View All Transactions <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400 text-sm">No transactions yet</div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, iconBg, iconColor, label, value, badge, badgeColor }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        {badge && <span className={`text-xs font-medium ${badgeColor} flex items-center gap-0.5`}><ArrowDownRight className="w-3 h-3" />{badge}</span>}
      </div>
      <p className="text-sm text-gray-500 mt-3">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
    </div>
  );
}
